import React, { useMemo, useState } from 'react'
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/20/solid'

export interface Column<Row> {
  key: string
  header: React.ReactNode
  render?: (row: Row) => React.ReactNode
  accessor?: (row: Row) => string | number
  sortable?: boolean
  align?: 'left' | 'right' | 'center'
  width?: string
}

interface DataTableProps<Row> {
  columns: Column<Row>[]
  rows: Row[]
  getRowKey: (row: Row) => string
  sort?: { key: string; dir: 'asc' | 'desc' }
  onSortChange?: (sort: { key: string; dir: 'asc' | 'desc' }) => void
  pagination?: {
    page: number
    pageSize: number
    total: number
    onPageChange: (page: number) => void
  }
  toolbar?: React.ReactNode
  rowActions?: (row: Row) => React.ReactNode
  onRowClick?: (row: Row) => void
  density?: 'comfortable' | 'compact'
  isLoading?: boolean
  loadingRows?: number
  empty?: React.ReactNode
  'data-testid'?: string
}

const headerClass =
  'sticky top-0 bg-gray-100 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-700 border-b border-gray-200 align-middle whitespace-nowrap'
const cellClass = 'px-3 text-sm text-gray-700 align-top'

const alignClass = (align?: 'left' | 'right' | 'center') =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DataTable = <Row extends Record<string, any>>({
  columns,
  rows,
  getRowKey,
  sort,
  onSortChange,
  pagination,
  toolbar,
  rowActions,
  onRowClick,
  density = 'comfortable',
  isLoading,
  loadingRows = 5,
  empty,
  'data-testid': testId,
}: DataTableProps<Row>) => {
  const densityClass = density === 'compact' ? 'py-1' : 'py-3'

  // Sort can be controlled (parent passes sort + onSortChange and sorts the rows) or
  // uncontrolled - if no onSortChange is given, manage the sort here and sort the rows
  // ourselves using the columns' accessors, so `sortable` columns work out of the box.
  const [internalSort, setInternalSort] = useState<{ key: string; dir: 'asc' | 'desc' } | undefined>(undefined)
  const effectiveSort = sort ?? internalSort
  const sortedRows = useMemo(() => {
    if (onSortChange || !effectiveSort) return rows
    const accessor = columns.find((c) => c.key === effectiveSort.key)?.accessor
    if (!accessor) return rows
    return [...rows].sort((a, b) => {
      const av = accessor(a)
      const bv = accessor(b)
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''))
      return effectiveSort.dir === 'asc' ? cmp : -cmp
    })
  }, [rows, columns, effectiveSort, onSortChange])

  if (isLoading) {
    return (
      <>
        {toolbar && <div className="mb-3">{toolbar}</div>}
        <div
          className="bg-white border border-gray-200 rounded-lg shadow-xs overflow-hidden"
          data-testid={testId}
        >
          <div className="space-y-1">
            {Array.from({ length: loadingRows }).map((_, idx) => (
              <div key={idx} className="h-12 bg-gray-100 animate-pulse" />
            ))}
          </div>
        </div>
      </>
    )
  }

  const handleSort = (key: string) => {
    const newDir = effectiveSort?.key === key && effectiveSort?.dir === 'asc' ? 'desc' : 'asc'
    if (onSortChange) onSortChange({ key, dir: newDir })
    else setInternalSort({ key, dir: newDir })
  }

  const totalPages = pagination ? Math.ceil(pagination.total / pagination.pageSize) : 0

  return (
    <>
      {/* The filter toolbar floats above the table card (consistent with the
          marketplaces) rather than living inside the card border. */}
      {toolbar && <div className="mb-3">{toolbar}</div>}
      <div
        className="bg-white border border-gray-200 rounded-lg shadow-xs overflow-hidden"
        data-testid={testId}
      >

      {rows.length === 0 ? (
        <div className="px-6 py-8 text-sm text-gray-500 italic text-center">
          {empty || 'No items'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                {columns.map((col) => {
                  const ariaSort = !col.sortable
                    ? undefined
                    : effectiveSort?.key === col.key
                      ? effectiveSort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  const sortIcon = !effectiveSort || effectiveSort.key !== col.key
                    ? <ChevronUpDownIcon className="h-3.5 w-3.5 text-gray-300" aria-hidden="true" />
                    : effectiveSort.dir === 'asc'
                      ? <ChevronUpIcon className="h-3.5 w-3.5 text-gray-600" aria-hidden="true" />
                      : <ChevronDownIcon className="h-3.5 w-3.5 text-gray-600" aria-hidden="true" />
                  return (
                    <th
                      key={col.key}
                      className={`${headerClass} ${alignClass(col.align)}`}
                      aria-sort={ariaSort}
                      style={{ width: col.width }}
                    >
                      {/* Flex-center the content so every header lines up vertically. Relying on the
                          inline baseline mis-aligns right-aligned sort buttons by ~2px (an inline-flex
                          element synthesises its baseline from its content). */}
                      <div className={`flex items-center ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : ''}`}>
                        {col.sortable ? (
                          <button
                            type="button"
                            onClick={() => handleSort(col.key)}
                            // Tailwind preflight resets `button { text-transform: none }`, so restate
                            // the inherited uppercase explicitly.
                            className="inline-flex items-center gap-0.5 uppercase hover:text-gray-700"
                          >
                            {col.align === 'right' && sortIcon}
                            {col.header}
                            {col.align !== 'right' && sortIcon}
                            <span className="sr-only">
                              {effectiveSort?.key === col.key
                                ? `sorted ${effectiveSort.dir === 'asc' ? 'ascending' : 'descending'}`
                                : 'not sorted'}
                            </span>
                          </button>
                        ) : (
                          col.header
                        )}
                      </div>
                    </th>
                  )
                })}
                {rowActions && (
                  // Pinned to the right so row actions stay visible when a wide table
                  // scrolls horizontally (otherwise the actions column slides off-screen
                  // and reads as "no actions"). Corner cell: sticky top AND right.
                  <th className={`${headerClass} text-right right-0 z-20`}>
                    <div className="flex items-center justify-end">Action</div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedRows.map((row) => (
                <tr
                  key={getRowKey(row)}
                  onClick={() => onRowClick?.(row)}
                  // Explicit bg-white on odd rows so the pinned action cell (bg-inherit)
                  // has a solid colour to match as content scrolls under it.
                  className={`bg-white even:bg-gray-50 ${onRowClick ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                >
                  {columns.map((col) => (
                    <td
                      key={`${getRowKey(row)}-${col.key}`}
                      className={`${cellClass} ${densityClass} ${alignClass(col.align)}`}
                      style={{ width: col.width }}
                    >
                      {col.render ? col.render(row) : col.accessor?.(row) ?? '-'}
                    </td>
                  ))}
                  {rowActions && (
                    <td className={`${cellClass} ${densityClass} whitespace-nowrap text-right sticky right-0 z-10 bg-inherit`}>
                      <div className="flex items-center justify-end gap-1.5">{rowActions(row)}</div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && rows.length > 0 && (
        <div
          className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-3 text-xs text-gray-600"
          role="contentinfo"
        >
          <p className="tabular-nums">
            Showing{' '}
            <span className="font-medium text-gray-900">
              {(pagination.page - 1) * pagination.pageSize + 1}
            </span>{' '}
            to{' '}
            <span className="font-medium text-gray-900">
              {Math.min(pagination.page * pagination.pageSize, pagination.total)}
            </span>{' '}
            of <span className="font-medium text-gray-900">{pagination.total}</span>
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 font-medium text-gray-700 shadow-xs hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Previous
            </button>
            <span className="px-2 tabular-nums text-gray-500">
              Page <span className="font-medium text-gray-900">{pagination.page}</span> of{' '}
              <span className="font-medium text-gray-900">{totalPages}</span>
            </span>
            <button
              type="button"
              disabled={pagination.page >= totalPages}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 font-medium text-gray-700 shadow-xs hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
      </div>
    </>
  )
}

export default DataTable
